<template>
    <div>
        <!-- Spotify Element -->
        <a v-if="this.spotify.is_playing != null" @click="openModal" class="cursor-pointer">
            <div class="soundbar">
                <div class="stroke"></div>
                <div class="stroke"></div>
                <div class="stroke"></div>
            </div>
            {{ currentTrackStr }} &nbsp; <strong>{{ this.spotify.track }}</strong> &nbsp; by &nbsp; <strong>{{
                this.spotify.artist }}</strong>
        </a>
        <a target="_blank" href="https://open.spotify.com/user/andikoenig91" v-else>
            <div class="soundbar">
                <div class="stroke"></div>
                <div class="stroke"></div>
                <div class="stroke"></div>
            </div>
            {{ currentTrackStr }}
        </a>

        <!-- Das Modal wird jetzt zum Body teleportiert -->
        <teleport to="body">
            <div v-if="showModal" class="modal-overlay">
                <!-- Die Klasse 'rounded' wird hier hinzugefügt, um die Ecken abzurunden -->
                <div class="modal-content rounded">
                    <!-- Der Schließen-Button wurde angepasst -->
                    <button @click="closeModal" class="modal-close-btn">&times;</button>
                    <!-- Neuer Wrapper für das responsive Layout des Inhalts -->
                    <div class="modal-inner-wrapper">
                        <div class="album-cover-wrapper">
                            <img v-if="albumCoverUrl" :src="albumCoverUrl" alt="Album Cover"
                                class="album-cover rounded" />
                        </div>
                        <div class="song-details-wrapper">
                            <h2 class="text-2xl font-bold mb-2 md:text-left">{{ spotify.track }}</h2>
                            <p class="text-lg mb-4 md:text-left">by <span class="font-bold">{{ spotify.artist }}</span>
                            </p>

                            <!-- Neue Struktur für die Informationszeilen mit Label über dem Wert -->
                            <div class="info-item" v-if="spotify.albumName">
                                <span class="info-label">{{ isPodcast ? 'Podcast:' : 'Album:' }}</span>
                                <!-- Albumtitel als Link zum Album -->
                                <a v-if="spotify.albumUrl" :href="spotify.albumUrl" target="_blank"
                                    class="info-value font-bold album-link">
                                    {{ spotify.albumName }}
                                </a>
                                <span v-else class="info-value font-bold">{{ spotify.albumName }}</span>
                            </div>
                            <div class="info-item" v-if="spotify.releaseDate">
                                <span class="info-label">Veröffentlicht:</span>
                                <span class="info-value font-bold">{{ formatReleaseDate(spotify.releaseDate) }}</span>
                            </div>
                            <div class="info-item" v-if="spotify.durationMs">
                                <span class="info-label">Dauer:</span>
                                <span class="info-value font-bold">{{ formatDuration(spotify.durationMs) }}</span>
                            </div>
                            <div class="info-item" v-if="spotify.popularity != null">
                                <span class="info-label">Popularität:</span>
                                <span class="info-value font-bold">{{ spotify.popularity }}%</span>
                            </div>
                            <!-- Der Spotify-Button wurde hierher verschoben -->
                            <a :href="spotify.href" target="_blank" class="spotify-button">
                                Auf Spotify anhören
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </teleport>
    </div>
</template>

<script>
import { getNowPlaying } from '@/utils/spotifyApi'

export default {
    data() {
        return {
            spotify: {
                is_playing: null,
                href: null,
                track: null,
                artist: null,
                albumName: null, // Neues Feld für Albumname
                albumUrl: null, // Neues Feld für Album-URL
                releaseDate: null, // Neues Feld für Veröffentlichungsdatum
                durationMs: null, // Neues Feld für Dauer in Millisekunden
                popularity: null // Neues Feld für Popularität
            },
            currentTrackStr: 'Nothing playing right now.',
            showModal: false, // Steuert die Sichtbarkeit des Modals
            albumCoverUrl: null, // Speichert die URL des Albumcovers
            isPodcast: false // true, wenn gerade eine Podcast-Folge läuft
        }
    },
    watch: {
        '$route.path'() {
            this.currentTrack()
        }
    },
    beforeMount() {
        this.currentTrack()
    },
    methods: {
        truncateText(text, length) {
            if (text.length <= length) {
                return text;
            }
            return text.substr(0, length) + '\u2026'
        },
        formatDuration(ms) {
            const minutes = Math.floor(ms / 60000);
            const seconds = ((ms % 60000) / 1000).toFixed(0);
            return minutes + ":" + (seconds < 10 ? '0' : '') + seconds;
        },
        formatReleaseDate(dateString) {
            if (!dateString) return '';
            // Spotify's release_date can be 'YYYY', 'YYYY-MM', or 'YYYY-MM-DD'
            const parts = dateString.split('-');
            if (parts.length === 3) {
                // YYYY-MM-DD
                const [year, month, day] = parts;
                return `${day}.${month}.${year}`;
            } else if (parts.length === 2) {
                // YYYY-MM
                const [year, month] = parts;
                return `01.${month}.${year}`; // Standardmäßig den ersten Tag des Monats
            } else if (parts.length === 1) {
                // YYYY
                const [year] = parts;
                return `01.01.${year}`; // Standardmäßig den ersten Januar des Jahres
            }
            return dateString; // Fallback, falls Format unerwartet
        },
        resetSpotify() {
            this.spotify.is_playing = null;
            this.spotify.href = null;
            this.spotify.track = null;
            this.spotify.artist = null;
            this.spotify.albumName = null;
            this.spotify.albumUrl = null;
            this.spotify.releaseDate = null;
            this.spotify.durationMs = null;
            this.spotify.popularity = null;
            this.albumCoverUrl = null;
            this.isPodcast = false;
        },
        async currentTrack() {
            try {
                const response = await getNowPlaying()
                if (response.status !== 200) {
                    this.resetSpotify();
                    this.currentTrackStr = 'Nothing playing right now.';
                    return;
                }

                const data = await response.json();
                const item = data.item;
                const np = data.is_playing;

                if (!item) {
                    this.resetSpotify();
                    this.currentTrackStr = 'Nothing playing right now.';
                    return;
                }

                const isPodcast = data.currently_playing_type === 'episode' || item.type === 'episode';
                this.isPodcast = isPodcast;
                this.spotify.is_playing = np;
                this.spotify.href = item.external_urls?.spotify ?? null;
                this.spotify.track = this.truncateText(item.name, 30);
                this.spotify.durationMs = item.duration_ms ?? null;

                if (isPodcast) {
                    // Podcast-Folge: andere Struktur (show statt album/artists, keine popularity).
                    const show = item.show || {};
                    this.currentTrackStr = np ? 'Now playing podcast:' : 'Last podcast:';
                    this.spotify.artist = this.truncateText(show.name || show.publisher || '', 30);
                    this.albumCoverUrl = item.images?.[0]?.url || show.images?.[0]?.url || null;
                    this.spotify.albumName = show.name || null;
                    this.spotify.albumUrl = show.external_urls?.spotify || null;
                    this.spotify.releaseDate = item.release_date || null;
                    this.spotify.popularity = null;
                } else {
                    this.currentTrackStr = np ? 'Now playing:' : 'Last played:';
                    this.spotify.artist = this.truncateText(item.artists.map(artist => artist.name).join(', '), 30);
                    this.albumCoverUrl = item.album?.images?.[0]?.url || null;
                    this.spotify.albumName = item.album?.name ?? null;
                    this.spotify.albumUrl = item.album?.external_urls?.spotify ?? null;
                    this.spotify.releaseDate = item.album?.release_date ?? null;
                    this.spotify.popularity = item.popularity ?? null;
                }
            } catch (e) {
                console.log('Couldn\'t fetch data from Spotify :(', e);
                this.resetSpotify();
                this.currentTrackStr = 'Couldn\'t fetch data :(';
            }
        },
        openModal() {
            // Die Bedingung wurde entfernt, sodass das Modal immer geöffnet wird,
            // wenn das Spotify-Element geklickt wird (vorausgesetzt, es gibt Daten).
            this.showModal = true;
        },
        closeModal() {
            this.showModal = false;
        }
    }
}
</script>

<style scoped lang="scss">
a {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    font-size: 1em;
    margin-left: 0;
}

.soundbar {
    height: 16px;
    display: flex;
    align-items: center;
    padding: initial !important;
    margin-right: 1em;

    .stroke {
        padding: initial !important;
        display: block;
        position: relative;
        background-color: #f1f1f1;
        height: 100%;
        width: 2px;
        border-radius: 1px;
        margin: 0 2px;
        animation: animSound 1.2s infinite linear;

        &:nth-child(1) {
            animation-delay: 0s;
        }

        &:nth-child(2) {
            animation-delay: 0.6s;
        }

        &:nth-child(3) {
            animation-delay: 0.9s;
        }
    }
}

@keyframes animSound {
    50% {
        height: 20%;
    }

    100% {
        height: 100%;
    }
}

.cursor-pointer {
    cursor: pointer !important
}

/* Modal Styles */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.75);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background-color: rgba(20, 21, 23, 0.95);
    color: white;
    padding: 2.5rem; // Reduzierter Padding für Mobile
    border-radius: 0.5rem;
    position: relative;
    width: 90%;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 400px;

    @media (min-width: 768px) {
        max-width: 750px; // Modal wird auf Desktop breiter
        padding: 2rem; // Ursprünglicher Padding für Desktop
    }
}

.modal-close-btn {
    position: absolute;
    top: 10px;
    right: 15px;
    background: none;
    border: none;
    font-size: 2rem;
    cursor: pointer;
    color: #fff;
    line-height: 1;
}

.modal-inner-wrapper {
    display: flex;
    flex-direction: column; // Mobile default: stacked
    align-items: flex-start; // Center items horizontally on mobile
    gap: 1.5rem; // Space between cover and details on mobile
    width: 100%; // Ensure it takes full width of modal-content

    @media (min-width: 768px) {
        // Desktop layout
        flex-direction: row; // Desktop: side-by-side
        text-align: left; // Text alignment for details
        gap: 2rem; // More space on desktop
    }
}

.album-cover-wrapper {
    flex-shrink: 0; // Prevent shrinking of the image wrapper

    @media (min-width: 768px) {
        // Auf Desktop soll der Wrapper die volle Höhe des Inhaltsbereichs einnehmen
        height: auto; // Oder eine feste Höhe, die zum Inhalt passt
        display: flex;
        align-items: flex-start; // Bild oben ausrichten
    }
}

.album-cover {
    width: 100%;
    max-width: 320px;
    height: auto;
    border-radius: 0.5rem;
    object-fit: cover;

    @media (min-width: 768px) {
        width: auto; // Deutlich größer auf Desktop
        height: 100%; // Deutlich größer auf Desktop
    }
}

.song-details-wrapper {
    flex-grow: 1; // Allow details to take up available space
    display: flex;
    flex-direction: column;
    align-items: flex-start; // Align text to start on both mobile and desktop

    h2 {
        // text-2xl from Tailwind
        text-align: left !important;
    }

    p {

        // text-lg, text-base from Tailwind
        &.mb-1 {
            margin-bottom: 0.25rem; // Fine-tune spacing for info lines
        }

        &.mb-4 {
            margin-bottom: 1rem; // Fine-tune spacing for last info line before button
        }
    }

    .info-item {
        display: flex;
        flex-direction: column; // Label über dem Wert (Mobile)
        align-items: flex-start; // Links ausgerichtet auf Mobilgeräten
        margin-bottom: 0.4rem; // Etwas weniger Abstand auf Mobilgeräten
        width: 100%; // Nimmt die volle Breite ein

        @media (min-width: 768px) {
            flex-direction: row; // Label und Wert nebeneinander (Desktop)
            justify-content: space-between; // Label links, Wert rechts
            align-items: baseline; // Baseline-Ausrichtung für Text
            margin-bottom: 0.5rem; // Etwas weniger Abstand auf Desktop
        }

        // Abstand für das letzte Info-Item vor dem Button
        &:last-of-type {
            margin-bottom: 1rem; // Reduzierter Abstand vor dem Button
        }
    }

    .info-label {
        font-size: 0.8em; // Kleinere Schrift für das Label
        opacity: 0.7; // Leicht ausgeblendet
        margin-bottom: 0.2em; // Kleiner Abstand zwischen Label und Wert (Mobile)
        flex-shrink: 0; // Verhindert Schrumpfen des Labels

        @media (min-width: 768px) {
            margin-bottom: 0; // Kein Abstand mehr auf Desktop
            margin-right: 1rem; // Abstand zum Wert auf Desktop
        }
    }

    .info-value {
        font-size: 1em; // Normale Schriftgröße für den Wert
        text-align: left; // Links ausgerichtet auf Mobilgeräten

        @media (min-width: 768px) {
            text-align: right; // Rechtsbündig auf Desktop
            flex-grow: 1; // Wert kann den restlichen Platz einnehmen
        }
    }

    .album-link {
        color: inherit;
        text-decoration: underline;
        display: inline-block;
        justify-self: flex-end;
        flex-grow: 0;

        &:hover {
            text-decoration: none;
        }
    }
}

.spotify-button {
    margin-top: 0.5rem; // Abstand zum Info-Block
    display: inline-block;
    border: 2px solid #1DB954;
    //background-color: #1DB954;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 9999px;
    text-decoration: none;
    font-weight: bold;
    transition: background-color 0.3s ease;

    &:before {
        background-color: #1DB954 !important; //Hover Effekt
    }

    &:hover {
        //background-color: #168a3f;
    }
}
</style>
